import {UUID} from 'crypto'
import {API_URL} from '../constants'

enum KegLocation {
  Inside = 'Inside',
  OutTheFront = 'Out the Front',
  OutTheBack = 'Out the Back',
}

interface VenueCollectionInfo {
  id: UUID
  customerId: UUID
  preferredCollectorId: UUID | null
  dateUpdated: string
  pickupInstruction: string
  venueOpenMonday: boolean
  venueOpenTuesday: boolean
  venueOpenWednesday: boolean
  venueOpenThursday: boolean
  venueOpenFriday: boolean
  startHour: number
  endHour: number
  kegLocation: KegLocation
  phoneNumber: string
}

export interface FetchVenuesResponse {
  customerId: UUID
  addressId: UUID
  name: string
  address1: string
  city: string
  stateOrProvince: string
  postalCode: string
  country: string
  latitude: number
  longitude: number
  distanceKm: number
  errorMessage?: string | null
  venueCollectionInfo?: VenueCollectionInfo | null
}

interface SubmitPickupRequestBody {
  addressId: UUID
  customerId: UUID
  endHour: number
  startHour: number
  kegLocation: KegLocation
  phoneNumber: string
  pickupInstruction: string | null
  quantity20L: number
  quantity30L: number
  quantity50L: number
  quantity9G: number
  venueOpenMonday: boolean
  venueOpenTuesday: boolean
  venueOpenWednesday: boolean
  venueOpenThursday: boolean
  venueOpenFriday: boolean
}

export class NetworkError extends Error {
  constructor() {
    super('Network request failed')
    this.name = 'NetworkError'
  }
}

export class HttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`HTTP error! status: ${status}`)
    this.name = 'HttpError'
    this.status = status
  }
}

// fetch rejects with TypeError when no HTTP response arrives (offline, dropped
// connection, DNS, or CORS). A Response with a non-2xx status is an API failure.
export async function fetchOk(url: string, init?: RequestInit): Promise<Response> {
  let response: Response
  try {
    response = await fetch(url, init)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }
    if (error instanceof TypeError) {
      throw new NetworkError()
    }
    throw error
  }

  if (!response.ok) {
    throw new HttpError(response.status)
  }

  return response
}

export const fetchVenuesServiceUtil = async (
  searchQuery: string
): Promise<FetchVenuesResponse[]> => {
  const response = await fetchOk(`${API_URL}/addresses/${searchQuery}`)
  try {
    return await response.json()
  } catch (error) {
    if (error instanceof TypeError) {
      throw new NetworkError()
    }
    throw error
  }
}

export const submitPickupRequestServiceUtil = async (
  pickupRequestBody: SubmitPickupRequestBody
): Promise<void> => {
  const response = await fetchOk(`${API_URL}/requestPickup`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(pickupRequestBody),
  })
  try {
    return await response.json()
  } catch (error) {
    if (error instanceof TypeError) {
      throw new NetworkError()
    }
    throw error
  }
}

export const fetchVenueByVenueIdServiceUtil = async (venueId: string, signal?: AbortSignal) => {
  const response = await fetchOk(`${API_URL}/collectors/venues?${new URLSearchParams({venueId})}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    signal,
  })

  return response.json()
}
